import { Label } from '@radix-ui/react-label';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useAppContext } from 'src/contexts/AppContext';
import { clearCatalogIdbStore } from 'src/contexts/CatalogDataContext/CatalogDataContext.utils';
import { Content } from 'src/contexts/ContentContext';
import {
  SITE_THEME_MODE_STORAGE_KEY,
  SiteThemeOption,
  SiteZoomOption,
  useSiteTheme,
} from 'src/contexts/SiteTheme/SiteThemeContext';
import { Checkbox } from 'src/core/interim/Checkbox';
import { PosSelect } from 'src/core/POS/PosSelect';
import { PosTextField } from 'src/core/POS/PosTextField';
import { Button, Stack, Switch, TooltipPopover } from 'src/core/ui';
import { clearIDBCache } from 'src/core/utils/createIndexDBPersister';
import { useUserHasAnyOfPermissions } from 'src/hooks/useUserHasAnyOfPermissions';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { useServerUserSetting } from 'src/hooks/useUserSetting';
import { IconsFill, InfoOutlineIcon } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import { SITE_THEME_OPTION_TO_CID_MAP } from 'src/utils/constants/contentIdMaps';
import { Feature, Permission, UserSetting } from 'src/WebApiController';

import { CurrencySection } from './CurrencySection';
import * as styles from './MyAccount.css';
import { TimezoneSection } from './TimezoneSection';

// TODO: Will convert elements to form
export const MyAccount = () => {
  const { loginContext } = useAppContext();
  const { isDarkMode } = useSiteTheme();
  const { isZoomSupported, selectedZoom, setSiteZoom } = useSiteTheme();
  const { selectedMode, setSiteMode } = useSiteTheme();
  const hasCurrencyConversionSettingsTabFeature = useUserHasFeature(
    Feature.CurrencyConversionSettingsTab
  );
  const hasEnableSelectionByDefaultSettingFeature = useUserHasFeature(
    Feature.EnableSelectionByDefaultSetting
  );
  const hasToggleNotificationsSettingsFeature = useUserHasFeature(
    Feature.ToggleNotificationsSettings
  );

  const {
    value: enableSelectionByDefault,
    setUserSetting: setEnableSelectionByDefault,
    isLoading: isLoadingEnableSelectionByDefaultSetting,
  } = useServerUserSetting<boolean>({
    id: UserSetting.EnableSelectionByDefault,
  });

  const {
    value: enableNotifications,
    setUserSetting: setEnableNotifications,
    isLoading: isLoadingEnableNotificationsSetting,
  } = useServerUserSetting<boolean>({
    id: UserSetting.EnableNotifications,
  });

  const userInformation = {
    name: loginContext?.user?.activeAccount?.accountName,
    email: loginContext?.user?.userName,
  };

  const hasAdminPermission = useUserHasAnyOfPermissions(
    Permission.SellerAccount_Admin
  );

  const queryClient = useQueryClient();

  const onZoomChange = useCallback(
    (value: string) => {
      if (value !== selectedZoom) setSiteZoom(value as SiteZoomOption);
    },
    [selectedZoom, setSiteZoom]
  );

  const onThemeChange = useCallback(
    (value: string) => {
      if (value !== selectedMode) setSiteMode(value as SiteThemeOption);
    },
    [selectedMode, setSiteMode]
  );

  const onEnableSelectionByDefaultChange = useCallback(
    (value: boolean) => {
      if (
        value !== enableSelectionByDefault &&
        hasEnableSelectionByDefaultSettingFeature
      ) {
        setEnableSelectionByDefault(value);
      }
    },
    [
      enableSelectionByDefault,
      setEnableSelectionByDefault,
      hasEnableSelectionByDefaultSettingFeature,
    ]
  );

  const zoomOptions = Object.values(SiteZoomOption).reduce(
    (options, current) => ({ ...options, [current]: current }),
    {}
  );
  return (
    <div>
      <div className={styles.accountInformation}>
        <div className={styles.sectionHeading}>
          <Content id={ContentId.AccountInformation} />
        </div>
        <Label className={styles.label}>
          <Content id={ContentId.Name} />
          <PosTextField value={userInformation.name} disabled />
        </Label>
        <Label className={styles.label}>
          <Content id={ContentId.Email} />
          <PosTextField value={userInformation.email} disabled />
        </Label>
      </div>
      <div className={styles.divider} />
      <div className={styles.passwordSection}>
        <div className={styles.sectionContainer}>
          <div className={styles.sectionHeading}>
            <Content id={ContentId.Password} />
          </div>
          <p className={styles.subtleText}>
            <Content id={ContentId.NeverShareYourPasswordWithAnyone} />
          </p>
        </div>
        <Button variant="outline" disabled>
          <Content id={ContentId.ChangePassword} />
        </Button>
      </div>
      <div className={styles.divider} />
      <div className={styles.sectionContainer}>
        <div className={styles.sectionHeading}>
          <Content id={ContentId.Display} />
        </div>
        <p className={styles.subtleText}>
          <Content id={ContentId.ChooseYourDisplaySettings} />
        </p>
        <Stack direction="column" gap="xl">
          {isZoomSupported && (
            <Label className={styles.label}>
              <Content id={ContentId.Zoom} />
              <PosSelect
                className={styles.select}
                value={selectedZoom}
                valueOptionsContent={zoomOptions}
                onChange={onZoomChange}
              />
            </Label>
          )}
          <Label className={styles.label}>
            <Content id={ContentId.Theme} />
            <PosSelect
              className={styles.select}
              value={selectedMode}
              valueOptionsContent={SITE_THEME_OPTION_TO_CID_MAP}
              onChange={onThemeChange}
            />
          </Label>
          {hasEnableSelectionByDefaultSettingFeature && (
            <Stack direction="row" gap="m" alignItems="center">
              <Checkbox
                disabled={isLoadingEnableSelectionByDefaultSetting}
                checked={enableSelectionByDefault || false}
                onChange={(e) => {
                  onEnableSelectionByDefaultChange(e.currentTarget.checked);
                }}
              />
              <div className={styles.label}>
                <Content id={ContentId.EnableSelectionByDefault} />
              </div>
            </Stack>
          )}
        </Stack>
        <Label className={styles.label}>
          <Content id={ContentId.Language} />
          <PosSelect
            disabled
            className={styles.select}
            defaultValue="english" // TODO: replace with map
            valueOptionsContent={{
              english: 'English', // TODO: replace with map
            }}
            onChange={() => {}}
          />
        </Label>
      </div>
      {hasAdminPermission && (
        <>
          <div className={styles.divider} />
          <TimezoneSection />
        </>
      )}

      <div className={styles.divider} />
      <div className={styles.sectionContainer} style={{ width: '50%' }}>
        <div className={styles.sectionHeading}>
          <Content id={ContentId.Other} />
        </div>
        <Label className={styles.label}>
          <Stack gap="l" justifyContent="spaceBetween">
            <Stack gap="m" alignItems="center">
              <Content id={ContentId.ClearBrowsingData} />
              <TooltipPopover
                contentVariant={isDarkMode ? 'light' : 'dark'}
                triggerContent={
                  <InfoOutlineIcon
                    withHoverEffect
                    fill={IconsFill.textWarning}
                  />
                }
              >
                <Content id={ContentId.ClearBrowsingDataHelp} />
              </TooltipPopover>
            </Stack>
            <Button
              style={{ width: 'fit-content' }}
              onClick={() => {
                queryClient.clear();
                clearIDBCache();
                clearCatalogIdbStore();
                localStorage.removeItem(SITE_THEME_MODE_STORAGE_KEY);
              }}
            >
              <Content id={ContentId.ClearData} />
            </Button>
          </Stack>
        </Label>

        {hasToggleNotificationsSettingsFeature && (
          <Label className={styles.label}>
            <Stack gap="l" justifyContent="spaceBetween">
              <Stack gap="m" alignItems="center">
                <Content id={ContentId.Notifications} />
                <TooltipPopover
                  contentVariant={isDarkMode ? 'light' : 'dark'}
                  triggerContent={
                    <InfoOutlineIcon
                      withHoverEffect
                      fill={IconsFill.textWarning}
                    />
                  }
                >
                  <Content id={ContentId.NotificationsHelp} />
                </TooltipPopover>
              </Stack>
              <Switch
                disabled={isLoadingEnableNotificationsSetting}
                checked={enableNotifications}
                onCheckedChange={setEnableNotifications}
              />
            </Stack>
          </Label>
        )}
      </div>
      <div className={styles.divider} />
      {!hasCurrencyConversionSettingsTabFeature && (
        <>
          <CurrencySection />
          <div className={styles.divider} />
        </>
      )}
      <div className={styles.sectionContainer}>
        <div className={styles.sectionHeading}>
          <Content id={ContentId.ExperimentalFeatures} />
        </div>
        <p className={styles.subtleText}>
          <Content id={ContentId.ExperimentalFeaturesComingSoon} />
        </p>
      </div>
    </div>
  );
};
